import { useMemo } from 'react'
import { Tour } from 'nextstepjs'
import { useCatalogAccess } from 'src/hook/use-catalog-access'
import { useFeaturedWorkouts } from 'src/hook/use-featured-workouts'
import { buildAppTourTours, getAppTourNavWorkoutsStepIndex } from './steps'

export type AppTourStepsState = {
  tours: Tour[]
  /** 0-based index of the Workouts nav step within `tours`. */
  navWorkoutsStepIndex: number
  /**
   * True until we know whether the Featured Workouts shelf will render for this account —
   * that answer decides whether step 1 exists at all, so the tour must not start while this
   * is true (see `AppTourController`).
   */
  isResolving: boolean
}

/**
 * Single source of truth for this session's app tour steps. The Featured Workouts step is
 * included only for accounts that will actually see the shelf on Home — not entitled to the
 * full catalog, and the featured set isn't empty (`HomeFeaturedWorkouts` /
 * `FeaturedWorkoutsSection`'s own render conditions, mirrored here). Every consumer that needs
 * the step list or a step's index (`AppTour`, `AppTourController`,
 * `useExpandNavbarForAppTourWorkoutsStep`) calls this instead of a static constant, so they
 * can never disagree about which steps exist for the current account.
 */
export function useAppTourSteps(): AppTourStepsState {
  const { isDefinitelyEntitled, isLoading: isCatalogAccessLoading } = useCatalogAccess()
  const { featuredWorkouts, isLoading: isFeaturedWorkoutsLoading } = useFeaturedWorkouts()

  const isResolving = isCatalogAccessLoading || isFeaturedWorkoutsLoading
  const includeFeaturedWorkouts = !isResolving && !isDefinitelyEntitled && featuredWorkouts.length > 0

  const tours = useMemo(() => buildAppTourTours(includeFeaturedWorkouts), [includeFeaturedWorkouts])
  const navWorkoutsStepIndex = useMemo(() => getAppTourNavWorkoutsStepIndex(tours), [tours])

  return { tours, navWorkoutsStepIndex, isResolving }
}
